import { useState, useRef, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Session-scoped "skip confirmation" flag (resets on hard reload)
let SKIP_CONFIRM = false;
export const setSkipConfirm = (v) => { SKIP_CONFIRM = !!v; };
export const getSkipConfirm = () => SKIP_CONFIRM;

export const useAIChat = (initialSessionId = null) => {
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(initialSessionId);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const loadSession = useCallback(async (sid) => {
    if (!sid) {
      setMessages([]);
      setSessionId(null);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/ai/sessions/${sid}/messages`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!res.ok) throw new Error('Failed to load session');
      const data = await res.json();
      setMessages(data.messages.map((m) => ({ role: m.role, content: m.content })));
      setSessionId(sid);
    } catch {
      setError('Failed to load conversation');
    }
  }, []);

  const newChat = useCallback(() => {
    setMessages([]);
    setSessionId(null);
    setError(null);
  }, []);

  const streamChat = useCallback(async (message) => {
    setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const res = await fetch(`${API_URL}/api/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ message, session_id: sessionId }),
        signal: controller.signal,
      });
      if (!res.ok) throw new Error('Request failed');
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          let evt;
          try { evt = JSON.parse(line.slice(6)); } catch { continue; }
          if (evt.type === 'session') {
            setSessionId(evt.session_id);
          } else if (evt.type === 'delta') {
            setMessages((prev) => {
              const next = [...prev];
              const last = next[next.length - 1];
              next[next.length - 1] = { ...last, content: last.content + evt.content };
              return next;
            });
          } else if (evt.type === 'error') {
            setError(evt.message);
          }
        }
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        setError('Failed to reach the AI assistant. Please try again.');
        setMessages((prev) => {
          const next = [...prev];
          if (next.length && next[next.length - 1].role === 'assistant' && !next[next.length - 1].content) next.pop();
          return next;
        });
      }
    } finally {
      abortRef.current = null;
    }
  }, [sessionId]);

  const executeAction = useCallback(async (action, docId) => {
    try {
      const res = await fetch(`${API_URL}/api/ai/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ action: { ...action, docId } }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Action failed');
      return { ok: true, history: data.history, doc: data.doc };
    } catch (e) {
      return { ok: false, error: e.message };
    }
  }, []);

  const confirmActionMessage = useCallback(async (msgIndex, docId) => {
    setMessages((prev) => {
      const next = [...prev];
      if (next[msgIndex]) next[msgIndex] = { ...next[msgIndex], confirming: true };
      return next;
    });
    const target = (await new Promise((r) => {
      setMessages((prev) => { r(prev[msgIndex]); return prev; });
    }));
    if (!target || !target.action) return;
    const result = await executeAction(target.action, docId);
    setMessages((prev) => {
      const next = [...prev];
      next[msgIndex] = {
        ...next[msgIndex],
        confirming: false,
        confirmed: result.ok,
        result,
      };
      // Append a follow-up assistant message
      next.push({
        role: 'assistant',
        content: result.ok
          ? `✅ Done. ${result.history || 'Update saved.'}`
          : `⚠️ Could not apply update: ${result.error || 'unknown error'}`,
      });
      return next;
    });
  }, [executeAction]);

  const cancelActionMessage = useCallback((msgIndex) => {
    setMessages((prev) => {
      const next = [...prev];
      if (next[msgIndex]) next[msgIndex] = { ...next[msgIndex], cancelled: true };
      next.push({ role: 'assistant', content: 'Okay, no changes made.' });
      return next;
    });
  }, []);

  const sendMessage = useCallback(
    async (text) => {
      const message = text.trim();
      if (!message || streaming) return;
      setError(null);
      setStreaming(true);
      setMessages((prev) => [...prev, { role: 'user', content: message }]);

      try {
        // Step 1: probe for action intent
        const intentRes = await fetch(`${API_URL}/api/ai/intent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({ message }),
        });
        const intent = intentRes.ok ? await intentRes.json() : { type: 'chat' };

        if (intent.type === 'action') {
          // Skip-confirm mode: only valid when a single doc matched
          if (SKIP_CONFIRM && intent.auto_select_id) {
            const result = await executeAction(intent.action, intent.auto_select_id);
            setMessages((prev) => [...prev, {
              role: 'assistant',
              content: result.ok
                ? `✅ ${intent.summary}\n\n${result.history || ''}`
                : `⚠️ Could not apply: ${result.error}`,
            }]);
          } else {
            setMessages((prev) => [...prev, {
              role: 'assistant',
              content: intent.summary,
              action: intent.action,
              matches: intent.matches,
              autoSelectId: intent.auto_select_id,
              isActionCard: true,
            }]);
          }
        } else if (intent.type === 'action_no_match') {
          setMessages((prev) => [...prev, { role: 'assistant', content: intent.summary }]);
        } else {
          // Fallback: regular streaming chat
          await streamChat(message);
        }
      } catch {
        setError('Failed to reach the AI assistant. Please try again.');
      } finally {
        setStreaming(false);
      }
    },
    [sessionId, streaming, streamChat, executeAction],
  );

  return {
    messages, sessionId, streaming, error,
    sendMessage, loadSession, newChat, setMessages,
    confirmActionMessage, cancelActionMessage,
  };
};
